import ColorLib from "tinycolor2";

import CanvasHelper from "./canvas_helper";
import globalStyles from "../../style/global_styles";
import { readCustomBimColors } from "./store_in_local_storage";

export type LegendItem = { color: ColorLib.ColorInput, text: string };

const fontSize = globalStyles.mediumFontSize;
const radius = fontSize / 2;
const space = globalStyles.space;

export default class LegendHelper extends CanvasHelper {
  customBimColors: boolean = false;
  selectedItems: LegendItem[] = [];
  nonSelectedItems: LegendItem[] = [];
  areAnyElementsSelected: boolean = false;
  areAnySelectedElementsDeviated: boolean = false;
  areAnySelectedElementsObstructing: boolean = false;
  orientation: "landscape" | "portrait";

  reset() {
    this.clear();
    this.setTransform();
    this.selectedItems.length = 0;
    this.nonSelectedItems.length = 0;
  }

  addSelectedItem(color: ColorLib.ColorInput, text: string) {
    this.selectedItems.push({ color, text });
  }

  addNonSelectedItem(color: ColorLib.ColorInput, text: string) {
    this.nonSelectedItems.push({ color, text });
  }

  drawLegendBackground(width: number, height: number) {
    this.drawRect(0, 0, width, height, 4, { r: 255, g: 255, b: 255, a: 0.75 });
  }

  drawLegendItem(legendItem: LegendItem, x: number, y: number) {
    this.context.save();

    this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);

    this.drawCircle(x + radius, y + radius, radius, legendItem.color);
    this.writeText(legendItem.text, x + radius * 2 + space, y + radius, "middle");

    this.context.restore();
  }

  /** Returns the width of the legend item (key + text) without spacing on either side */
  private getLegendItemWidth(legendItem: LegendItem): number {
    this.context.save();

    this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);

    const width = radius * 2 + space + this.context.measureText(legendItem.text).width;

    this.context.restore();

    return width;
  }

  render = () => {
    this.context.save();
    const { width, height } = this.getSize();

    this.drawLegendBackground(width, height);

    let offsetX = space;
    let offsetY = space;

    if (this.orientation === "landscape") {
      if (this.areAnyElementsSelected) {
        this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
        this.writeText("Selected", space, offsetY + radius, "middle");
        offsetY += space + radius;
        this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);
        for (let i = 0; i < this.selectedItems.length; i += 2) {
          this.drawLegendItem(this.selectedItems[i], offsetX, offsetY);
          if (this.selectedItems[i + 1]) {
            this.drawLegendItem(this.selectedItems[i + 1], offsetX, offsetY + fontSize + space);
            offsetX += Math.max(this.getLegendItemWidth(this.selectedItems[i]), this.getLegendItemWidth(this.selectedItems[i + 1]));
          } else {
            offsetX += this.getLegendItemWidth(this.selectedItems[i]);
          }
          offsetX += space;
        }

        this.drawLine(offsetX, space, offsetX, height - space, globalStyles.darkGray);
        offsetX += space;

        offsetY -= space + radius;

        this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
        this.writeText("Non-Selected", offsetX, offsetY + radius, "middle");
        offsetY += space + radius;
      }

      for (let i = 0; i < this.nonSelectedItems.length; i += 2) {
        this.drawLegendItem(this.nonSelectedItems[i], offsetX, offsetY);
        if (this.nonSelectedItems[i].text === "Obstructed") {
          this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
          // we want to tuck this under the last item
          this.writeText("Not Built", offsetX + radius * 2 + space, offsetY + fontSize + 2, "middle");
        }
        if (this.nonSelectedItems[i + 1]) {
          this.drawLegendItem(this.nonSelectedItems[i + 1], offsetX, offsetY + fontSize + space);
          if (this.nonSelectedItems[i + 1].text === "Obstructed") {
            this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
            // we want to tuck this under the last item
            this.writeText("Not Built", offsetX + radius * 2 + space, offsetY + fontSize + space + fontSize + 2, "middle");
          }
          offsetX += Math.max(this.getLegendItemWidth(this.nonSelectedItems[i]), this.getLegendItemWidth(this.nonSelectedItems[i + 1]));
        } else {
          offsetX += this.getLegendItemWidth(this.nonSelectedItems[i]);
        }
        offsetX += space;
      }
    } else {
      if (this.areAnyElementsSelected) {
        this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
        this.writeText("Selected", space, offsetY + radius, "middle");

        this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);
        offsetY += space + radius;
        this.selectedItems.forEach((legendItem) => {
          this.drawLegendItem(legendItem, offsetX, offsetY);
          offsetY += fontSize + space;
        });

        this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);
        offsetY += space;

        this.drawLine(offsetX, offsetY, 100, offsetY, globalStyles.darkGray);

        offsetY += space;
        this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
        this.writeText("Non-Selected", space, offsetY + radius, "middle");
        offsetY += space * 2;
        this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);
      }
      this.nonSelectedItems.forEach((legendItem) => {
        this.drawLegendItem(legendItem, offsetX, offsetY);
        offsetY += fontSize + space;
        if (legendItem.text === "Obstructed") {
          this.setTextStyle(globalStyles.mediumGray, fontSize - 6, "Roboto, sans-serif", 400);
          // we want to tuck this under the last item
          offsetY += 0.1;
          this.writeText("Not Built", offsetX + radius * 2 + space, offsetY, "middle");
          this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);
        }
      });
    }

    this.context.restore();
  };

  getSize = () => {
    this.context.save();

    this.setTextStyle(globalStyles.mediumGray, fontSize, "Roboto, sans-serif", 400);
    let maxLineWidth = 0;

    if (this.orientation === "landscape") {  // 2 rows w/ as many columns as needed
      let firstLineWidth = space;
      for (let i = 0; i < this.selectedItems.length; i += 2) {
        if (this.selectedItems[i]) {
          firstLineWidth += this.getLegendItemWidth(this.selectedItems[i]) + space;
        }
      }
      let secondLineWidth = space;
      for (let i = 1; i < this.selectedItems.length; i += 2) {
        if (this.selectedItems[i]) {
          secondLineWidth += this.getLegendItemWidth(this.selectedItems[i]) + space;
        }
      }

      const widthSoFar = Math.max(firstLineWidth, secondLineWidth);
      firstLineWidth = widthSoFar + space * 2;
      for (let i = 0; i < this.nonSelectedItems.length; i += 2) {
        if (this.nonSelectedItems[i]) {
          firstLineWidth += this.getLegendItemWidth(this.nonSelectedItems[i]) + space;
        }
      }
      secondLineWidth = widthSoFar + space * 2;
      for (let i = 1; i < this.nonSelectedItems.length; i += 2) {
        if (this.nonSelectedItems[i]) {
          secondLineWidth += this.getLegendItemWidth(this.nonSelectedItems[i]) + space;
        }
      }

      maxLineWidth = Math.max(firstLineWidth, secondLineWidth);

      const height =
        space +
        2 * (fontSize + space) +
        space + radius;

      this.context.restore();
      return { width: maxLineWidth, height: height };
    } else {
      this.selectedItems.forEach((legendItem) => {
        const lineWidth = space + this.getLegendItemWidth(legendItem) + space;
        maxLineWidth = Math.max(maxLineWidth, lineWidth);
      });

      this.nonSelectedItems.forEach((legendItem) => {
        const lineWidth = space + this.getLegendItemWidth(legendItem) + space;
        maxLineWidth = Math.max(maxLineWidth, lineWidth);
      });

      const totalRows = this.selectedItems.length + this.nonSelectedItems.length;
      const height =
        space +
        totalRows * (fontSize + space) +
        space * 8;

      this.context.restore();
      return { width: maxLineWidth, height };
    }
  };

  drawLegendForScreenshot() {
    this.reset();
    if (this.customBimColors) {
      const colors = readCustomBimColors();
      if (this.areAnyElementsSelected) {
        this.addSelectedItem(globalStyles.selectionBlue, "As-Designed");
        if (this.areAnySelectedElementsDeviated) {
          this.addSelectedItem(colors.AsBuilt, "As-Built");
        }
      }
      this.addNonSelectedItem(colors.NotBuilt, "Not-Built");
      this.addNonSelectedItem(colors.InPlace, "Built");
      this.addNonSelectedItem(colors.DeviationAsDesigned, "Deviated");
      this.addNonSelectedItem(colors.ClashingAsDesigned, "Clashing");
      if (this.areAnySelectedElementsObstructing) {
        this.addNonSelectedItem(colors.Obstructed, "Obstructed");
      }
    } else {
      if (this.areAnyElementsSelected) {
        this.addSelectedItem(globalStyles.selectionBlue, "As-Designed");
        if (this.areAnySelectedElementsDeviated) {
          this.addSelectedItem(globalStyles["BIM/As Built"], "As-Built");
        }
      }
      this.addNonSelectedItem(globalStyles["BIM/Not Built"], "Not-Built");
      this.addNonSelectedItem(globalStyles["BIM/Built"], "Built");
      this.addNonSelectedItem(globalStyles["BIM/Deviation"], "Deviated");
      this.addNonSelectedItem(globalStyles["BIM/Clash"], "Clashing");
      if (this.areAnySelectedElementsObstructing) {
        this.addNonSelectedItem(globalStyles["BIM/Obstructed"], "Obstructed");
      }
    }
    const { width, height } = this.getSize();
    this.canvas.width = width;
    this.canvas.height = height;
    this.render();
  }
}
